/*
 * Tenue — product-info.js
 * Variant selection and product details updates.
 */
(function () {
  'use strict';

  const Tenue = window.Tenue;

  /* ------------------------------------------------------------------------
     <variant-picker>
     ------------------------------------------------------------------------ */
  if (!customElements.get('variant-picker')) {
    class VariantPicker extends HTMLElement {
      get groups() {
        return Array.from(this.querySelectorAll('[data-option-group]'));
      }

      selectedElement(group) {
        const select = group.querySelector('select');
        if (select) return select.options[select.selectedIndex];
        return group.querySelector('input:checked');
      }

      get selectedOptionValueIds() {
        return this.groups
          .map((group) => this.selectedElement(group))
          .filter((element) => element && element.dataset.optionValueId)
          .map((element) => element.dataset.optionValueId);
      }

      get selectedOptions() {
        return this.groups
          .map((group) => {
            const element = this.selectedElement(group);
            return element ? { name: group.dataset.optionName || '', value: element.value } : null;
          })
          .filter(Boolean);
      }

      get selectedVariant() {
        const script = this.querySelector('[data-selected-variant]');
        if (!script) return null;
        try {
          return JSON.parse(script.textContent);
        } catch (error) {
          return null;
        }
      }
    }
    customElements.define('variant-picker', VariantPicker);
  }

  /* ------------------------------------------------------------------------
     <product-info>: reloads product details when options change.
     ------------------------------------------------------------------------ */
  if (!customElements.get('product-info')) {
    class ProductInfo extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.dataset.sectionId;
        this.onChange = (event) => this.handleChange(event);
        this.addEventListener('change', this.onChange);
      }

      disconnectedCallback() {
        this.removeEventListener('change', this.onChange);
        if (this.abortController) this.abortController.abort();
      }

      handleChange(event) {
        const picker = event.target.closest('variant-picker');
        if (!picker || !this.contains(picker)) return;

        const element =
          event.target.tagName === 'SELECT' ? event.target.options[event.target.selectedIndex] : event.target;
        if (!element || !element.dataset.optionValueId) return;

        const productUrl = element.dataset.productUrl || this.dataset.url;
        const selectDeferred = this.dispatchSelectEvent(picker);
        this.renderVariant(productUrl, picker.selectedOptionValueIds, event.target.id, selectDeferred);
      }

      async renderVariant(productUrl, optionValueIds, focusId, selectDeferred) {
        if (this.abortController) this.abortController.abort();
        this.abortController = new AbortController();

        const params = new URLSearchParams({ section_id: this.sectionId });
        if (optionValueIds.length) params.set('option_values', optionValueIds.join(','));
        this.setAttribute('aria-busy', 'true');

        try {
          const response = await fetch(`${productUrl}?${params.toString()}`, {
            signal: this.abortController.signal,
          });
          const html = Tenue.parseHTML(await response.text());
          const incoming = html.getElementById(`ProductInfo-${this.sectionId}`);
          if (!incoming) throw new Error('Product section not found in response');

          const incomingPicker = incoming.querySelector('variant-picker');
          const variant = incomingPicker ? incomingPicker.selectedVariant : null;

          if (productUrl !== this.dataset.url) {
            this.replaceWith(incoming);
            if (window.Shopify && window.Shopify.PaymentButton) window.Shopify.PaymentButton.init();
            if (incoming.dataset.updateUrl === 'true') this.updateUrl(productUrl, variant);
            this.resolveSelect(selectDeferred, variant, incoming);
            const focusTarget = document.getElementById(focusId);
            if (focusTarget) focusTarget.focus();
            return;
          }

          this.updateParts(incoming);
          this.updateBuyButtons(incoming, variant);

          if (variant && variant.featured_media) {
            const gallery = this.querySelector('media-gallery');
            if (gallery) gallery.setActiveMedia(variant.featured_media.id);
          }

          const pickup = this.querySelector('pickup-availability');
          if (pickup && typeof pickup.update === 'function') pickup.update(variant);

          if (this.dataset.updateUrl === 'true') this.updateUrl(productUrl, variant);
          this.resolveSelect(selectDeferred, variant, this);
          Tenue.emit('variant:change', { sectionId: this.sectionId, variant });

          const focusTarget = document.getElementById(focusId);
          if (focusTarget) focusTarget.focus();
        } catch (error) {
          if (selectDeferred) selectDeferred.reject(error);
          if (error.name !== 'AbortError') console.error(error);
        } finally {
          this.removeAttribute('aria-busy');
        }
      }

      updateParts(incoming) {
        this.querySelectorAll('[data-product-part]').forEach((part) => {
          if (!part.id || part.dataset.productPart === 'buy-buttons') return;
          const source = incoming.querySelector(`#${CSS.escape(part.id)}`);
          if (source) part.innerHTML = source.innerHTML;
        });
      }

      updateBuyButtons(incoming, variant) {
        const buttonId = `ProductSubmit-${this.sectionId}`;
        const button = this.querySelector(`#${CSS.escape(buttonId)}`);
        const incomingButton = incoming.querySelector(`#${CSS.escape(buttonId)}`);

        if (button && incomingButton) {
          button.disabled = incomingButton.disabled;
          const label = button.querySelector('[data-button-label]');
          const incomingLabel = incomingButton.querySelector('[data-button-label]');
          if (label && incomingLabel) label.textContent = incomingLabel.textContent;
        }

        const form = document.getElementById(`ProductForm-${this.sectionId}`);
        if (form) {
          const input = form.querySelector('input[name="id"]');
          if (input) {
            input.value = variant ? variant.id : '';
            input.disabled = !variant || Boolean(button && button.disabled);
            input.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }

        const productForm = this.querySelector('product-form');
        if (productForm && typeof productForm.clearError === 'function') productForm.clearError();

        const ctaButton = this.querySelector('[data-cta-bar-button]');
        if (ctaButton && button) ctaButton.disabled = button.disabled;
      }

      updateUrl(productUrl, variant) {
        const url = variant ? `${productUrl}?variant=${variant.id}` : productUrl;
        window.history.replaceState({}, '', url);
      }

      dispatchSelectEvent(picker) {
        const events = window.StandardEvents || {};
        if (!events.ProductSelectEvent) return null;
        const deferred = events.ProductSelectEvent.createPromise();
        picker.dispatchEvent(
          new events.ProductSelectEvent({
            product: {
              id: this.dataset.productId,
              title: this.dataset.productTitle,
              handle: this.dataset.productHandle,
            },
            selectedOptions: picker.selectedOptions,
            promise: deferred.promise,
          })
        );
        return deferred;
      }

      resolveSelect(deferred, variant, root) {
        if (!deferred) return;
        if (!variant) {
          deferred.resolve({ variant: null });
          return;
        }
        const picker = root.querySelector('variant-picker');
        deferred.resolve({
          variant: {
            id: String(variant.id),
            title: variant.title,
            availableForSale: Boolean(variant.available),
            price: {
              amount: (variant.price / 100).toFixed(2),
              currencyCode: this.dataset.currency,
            },
            selectedOptions: picker ? picker.selectedOptions : [],
          },
        });
      }
    }
    customElements.define('product-info', ProductInfo);
  }
})();
